import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { BOOK } from "@/data/book";
import TableOfContents from "@/components/TableOfContents";
import ChapterArticle from "@/components/ChapterArticle";
import ShareRow from "@/components/ShareRow";
import MobileTocDrawer from "@/components/MobileTocDrawer";
import { ChevronUp, Menu, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/api";

// Stable chapter objects (BOOK is a module constant, never mutated).
const PROLOGUE_CHAPTER = { title: BOOK.introTitle, body: BOOK.intro };
const CLOSING_CHAPTER = { title: BOOK.closingTitle, body: BOOK.closing };

function useActiveSection(initialId = "before-you-begin") {
  const [activeId, setActiveId] = useState(initialId);
  const observerRef = useRef(null);

  useEffect(() => {
    const elements = document.querySelectorAll("article[id]");
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    elements.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
    // Effect intentionally runs once on mount; all referenced names are locals or refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [activeId, setActiveId];
}

function useReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const pct = h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight);
      setProgress(Math.min(1, Math.max(0, pct)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
    // No external deps; onScroll, h, pct are local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return progress;
}

function ReaderHeader({ baseUrl }) {
  return (
    <header className="mb-16">
      <p className="font-ui text-[11px] uppercase tracking-[0.35em] text-primary">A field guide</p>
      <h1 className="font-display text-5xl md:text-6xl text-foreground leading-[1.02] tracking-tight mt-3">
        {BOOK.title}
      </h1>
      <p className="mt-4 font-display italic text-2xl text-secondary">{BOOK.subtitle}</p>
      <p className="mt-2 font-ui text-sm text-muted-foreground">by {BOOK.author}</p>
      <div className="wobble-divider mt-6" />
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Button asChild variant="outline" className="rounded-full font-ui" data-testid="reader-download">
          <a href={`${API_BASE}/book/pdf`} target="_blank" rel="noreferrer">
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </a>
        </Button>
        <ShareRow title={BOOK.title} chapterUrl={baseUrl} testIdPrefix="share-book" />
      </div>
    </header>
  );
}

export default function Reader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useActiveSection();
  const [tocOpen, setTocOpen] = useState(false);
  const progress = useReadingProgress();

  const goTo = useCallback((id) => {
    setTocOpen(false);
    setActiveId(id);
    navigate({ hash: id });
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    // setTocOpen is a stable useState setter; el/id are local to this callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, setActiveId]);

  useEffect(() => {
    if (!location.hash) { window.scrollTo(0, 0); return; }
    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      // IntersectionObserver in useActiveSection will update activeId once we land there.
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
    // id and el are computed inside the effect; only location.hash drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const baseUrl = useMemo(
    () => (typeof window !== "undefined" ? window.location.origin + window.location.pathname : ""),
    []
  );

  return (
    <main data-testid="reader-page" className="paper-grain">
      <div className="reading-progress" style={{ width: `${progress * 100}%` }} data-testid="reading-progress" />

      <button
        type="button"
        onClick={() => setTocOpen(true)}
        data-testid="mobile-toc-trigger"
        className="lg:hidden fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-secondary text-secondary-foreground shadow-lg flex items-center justify-center"
        aria-label="Open table of contents"
      >
        <Menu className="h-5 w-5" />
      </button>

      {tocOpen && <MobileTocDrawer activeId={activeId} onSelect={goTo} onClose={() => setTocOpen(false)} />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-32">
        <div className="grid lg:grid-cols-[260px_1fr] gap-12">
          <TableOfContents activeId={activeId} onSelect={goTo} />

          <div>
            <ReaderHeader baseUrl={baseUrl} />

            <ChapterArticle
              chapter={PROLOGUE_CHAPTER}
              prefix="Prologue"
              anchorId="before-you-begin"
              hideComments={true}
              image={BOOK.images.intro}
              imageAlt="Sun peeking over mountain"
            />

            {BOOK.chapters.map((ch) => (
              <ChapterArticle
                key={ch.id}
                chapter={ch}
                prefix={`Truth ${String(ch.number).padStart(2, "0")}`}
                anchorId={ch.id}
                pullQuote={ch.pullQuote}
                image={ch.image}
                imageAlt={ch.title}
              />
            ))}

            <ChapterArticle
              chapter={CLOSING_CHAPTER}
              prefix="Epilogue"
              anchorId="in-closing"
              hideComments={true}
              image={BOOK.images.tree}
              imageAlt="Solitary tree in fog"
            />

            <div className="mt-16 border-t border-border pt-12 text-center max-w-prose mx-auto">
              <p className="font-display italic text-3xl text-secondary leading-snug">
                If something here helped — pass it on. Quietly is fine.
              </p>
              <div className="wobble-divider mx-auto my-6" />
              <ShareRow title={BOOK.title} chapterUrl={baseUrl} testIdPrefix="share-final" />
              <p className="font-ui text-sm text-muted-foreground mt-8">
                Want a copy to keep? <Link to="/#signup" className="text-primary link-soft">Email me the PDF</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        data-testid="back-to-top"
        className="fixed bottom-6 left-6 z-40 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors"
        aria-label="Back to top"
      >
        <ChevronUp className="h-4 w-4" />
      </button>
    </main>
  );
}
